import AttachFileIcon from '@mui/icons-material/AttachFile'
import { Alert, Box, Chip, CircularProgress, Typography } from '@mui/material'
import { FC, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { ChatMessage, ChatStatus } from '../../../stores/chatStore'
import MarkdownComponent from '../../molecules/Markdown/Markdown'

interface AiChatMessageListProps {
  messages: ChatMessage[]
  status: ChatStatus
  rateLimited: boolean
  // In Hermine gepflegte Willkommensnachricht des aktuell gewählten Agenten —
  // im leeren Chat als Assistant-Bubble gezeigt (rein clientseitig, nicht
  // persistiert). null/undefined = Fallback auf den statischen Hinweis.
  welcomeMessage?: string | null
}

/**
 * Reine Anzeige der Chat-Historie (kein Tool-Event-Rendering im MVP, siehe
 * Task-7-Brief). Zeigt zusätzlich den "Agent arbeitet"-Zustand während
 * status === 'waiting' sowie eine Fehlermeldung bei status === 'error'.
 */
// Markdown-Feinschliff der Assistant-Bubbles — geteilt zwischen echten
// AI-Messages und der clientseitigen Willkommensnachricht.
const markdownBodySx = {
  '& p, & ul, & ol, & pre, & table, & blockquote': { my: 0.5 },
  '& > div > :first-of-type': { mt: 0 },
  '& > div > :last-child': { mb: 0 },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    my: 1,
    fontSize: '0.95rem',
    fontWeight: 600
  },
  '& ul, & ol': { pl: 2.5 },
  '& pre': { overflowX: 'auto' },
  '& code': { fontSize: '0.8rem' },
  '& a': { color: 'primary.main' }
}

const AiChatMessageList: FC<AiChatMessageListProps> = ({
  messages,
  status,
  rateLimited,
  welcomeMessage
}) => {
  const { t } = useTranslation('ai')
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, status])

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        overflowY: 'auto',
        // Padding innen statt außen: die Liste selbst (inkl. Scrollbar)
        // reicht bis an die Drawer-Kante.
        px: 2,
        py: 1
      }}
    >
      {messages.length === 0 &&
        status !== 'waiting' &&
        status !== 'error' &&
        (welcomeMessage ? (
          <Box
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: 'grey.100',
              color: 'text.primary',
              borderRadius: 2,
              px: 2,
              py: 1,
              maxWidth: '85%'
            }}
          >
            <Typography variant="body2" component="div" sx={markdownBodySx}>
              <MarkdownComponent>{welcomeMessage}</MarkdownComponent>
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('chat.empty')}
          </Typography>
        ))}

      {messages.map(message => (
        <Box
          key={message.id}
          sx={{
            alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
            backgroundColor: message.role === 'user' ? 'primary.main' : 'grey.100',
            color: message.role === 'user' ? 'common.white' : 'text.primary',
            borderRadius: 2,
            px: 2,
            py: 1,
            maxWidth: '85%'
          }}
        >
          {message.role === 'user' ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {message.content}
            </Typography>
          ) : (
            <Typography variant="body2" component="div" sx={markdownBodySx}>
              <MarkdownComponent>{message.content}</MarkdownComponent>
            </Typography>
          )}
          {(message.uploads?.length ?? 0) > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {message.uploads?.map(upload => (
                <Chip
                  key={`${message.id}-${upload.filename}`}
                  size="small"
                  icon={<AttachFileIcon />}
                  label={upload.filename}
                  component={upload.url ? 'a' : 'div'}
                  href={upload.url ?? undefined}
                  target="_blank"
                  clickable={!!upload.url}
                  sx={{ maxWidth: '100%' }}
                />
              ))}
            </Box>
          )}
        </Box>
      ))}

      {status === 'waiting' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start' }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t('chat.waiting')}
          </Typography>
        </Box>
      )}

      {status === 'error' && (
        <Alert severity="error">{rateLimited ? t('chat.rateLimited') : t('chat.error')}</Alert>
      )}

      <div ref={bottomRef} />
    </Box>
  )
}

export default AiChatMessageList
